import type { EncodingTransform } from "@src/types/dashboards";
import { parseUserNumber } from "./parse-user-number";

/** Default multiplier — an unset field renders exactly as fetched. */
export const DEFAULT_SCALE = 1;

/**
 * How many significant digits a scaled value keeps. Multiplying decimals in
 * binary floating point leaves a tail of noise: 3454 times 0.0001 comes out
 * as 0.34540000000000004. Keeping 12 significant digits removes that noise.
 * Values that carry more than 12 significant digits do lose their tail.
 */
const SCALED_PRECISION = 12;

/** Strip the floating-point noise a multiplication leaves behind. */
const roundScaled = (value: number): number =>
  Number(value.toPrecision(SCALED_PRECISION));

/**
 * Turn the drawer's per-field inputs into the wire `encoding.transforms`.
 * Entries that are blank, invalid, or the default `1` are dropped, so a
 * widget the user never scaled stays byte-identical to before this feature.
 */
export const buildScaleTransforms = (
  entries: { field: string; scale: string }[],
): EncodingTransform[] => {
  const out: EncodingTransform[] = [];
  for (const { field, scale } of entries) {
    const value = parseUserNumber(scale);
    if (value === null || value === DEFAULT_SCALE) continue;
    out.push({ field, op: "scale", value });
  }
  return out;
};

/**
 * Merge freshly built scale transforms into an existing list, replacing only
 * the scale entries for the fields the caller owns and keeping every other
 * transform (other ops, unmanaged fields) untouched. Lets a widget save
 * rewrite its own scales without dropping transforms it doesn't understand.
 */
export const mergeTransforms = (
  existing: EncodingTransform[] | null | undefined,
  ownedFields: Iterable<string>,
  built: EncodingTransform[],
): EncodingTransform[] => {
  const owned = new Set(ownedFields);
  const preserved = (existing ?? []).filter(
    (t) => !(t.op === "scale" && owned.has(t.field)),
  );
  return [...preserved, ...built];
};

/** `{ transforms }` when there is something to persist, or `{}` (key
 *  omitted) when the widget never had transforms and nothing was set — so
 *  an unset widget keeps the exact key set it had before scaling existed. */
export const transformsKey = (
  transforms: EncodingTransform[] | null | undefined,
  existing: EncodingTransform[] | null | undefined,
): { transforms?: EncodingTransform[] } => {
  if (transforms == null) return {};
  if (transforms.length === 0 && existing == null) return {};
  return { transforms };
};

/** Merge an editor's scale entries into `existing`, preserving transforms the
 *  editor doesn't own, and return the encoding slice to spread onto the save
 *  body. */
export const scaleTransformsKey = (
  existing: EncodingTransform[] | null | undefined,
  ownedFields: Iterable<string>,
  built: EncodingTransform[],
): { transforms?: EncodingTransform[] } =>
  transformsKey(mergeTransforms(existing, ownedFields, built), existing);

/**
 * Save-side helper shared by the editors where every row owns its own
 * `value_<index>` scale (multi-line and multiple-text-values).
 *
 * The fields it claims are the union of the widget's saved bindings and the
 * rows on screen right now. Claiming both is what drops the scale of a row
 * the user deleted. Factors equal to the default are dropped as well, so a
 * widget nobody scaled keeps the transforms it had before scaling existed.
 *
 * Returns the `{ transforms? }` slice, ready to spread into `encoding`.
 */
export const buildScaleTransformsForEdits = (
  existing: EncodingTransform[] | null | undefined,
  bindingsCount: number,
  items: { scale: string }[],
): { transforms?: EncodingTransform[] } =>
  scaleTransformsKey(
    existing,
    Array.from(
      { length: Math.max(bindingsCount, items.length) },
      (_v, index) => `value_${index}`,
    ),
    buildScaleTransforms(
      items.map((item, index) => ({
        field: `value_${index}`,
        scale: item.scale,
      })),
    ),
  );

/** The scale factor for one field, defaulting to 1 when none is stored. */
export const scaleForField = (
  field: string | undefined,
  transforms: EncodingTransform[] | null | undefined,
): number => {
  if (!field || !transforms) return DEFAULT_SCALE;
  const match = transforms.find(
    (t) => t.field === field && t.op === "scale" && t.value != null,
  );
  return match?.value ?? DEFAULT_SCALE;
};

/**
 * Apply a field's stored transforms to one fetched value. Only numeric
 * values are scaled. Strings (status and mode nodes) and nulls pass through
 * untouched, so a scale can never corrupt a text widget.
 */
export const applyFieldTransforms = (
  field: string | undefined,
  value: number | string | null,
  transforms: EncodingTransform[] | null | undefined,
): number | string | null => {
  if (typeof value !== "number") return value;
  const factor = scaleForField(field, transforms);
  if (factor === DEFAULT_SCALE) return value;
  return roundScaled(value * factor);
};

/**
 * Apply a field's scale to a whole numeric series (the line charts). Gaps
 * (`null`) stay gaps. Non-mutating: the input array is returned unchanged for
 * the default factor, otherwise a scaled copy — so editing the factor
 * recomputes from the raw fetched values.
 */
export const applyScaleToNumbers = (
  field: string | undefined,
  values: (number | null)[],
  transforms: EncodingTransform[] | null | undefined,
): (number | null)[] => {
  const factor = scaleForField(field, transforms);
  if (factor === 1) return values;
  return values.map((value) =>
    typeof value === "number" ? roundScaled(value * factor) : value,
  );
};

/**
 * Apply a field's scale to the aggregated min/max bound series. A negative
 * factor reverses ordering, so the scaled raw maximum becomes the displayed
 * minimum (and vice-versa) — otherwise "show min" would draw the maximum.
 * Either bound absent (raw mode) returns both untouched.
 */
export const applyScaleToBounds = (
  field: string | undefined,
  minValues: (number | null)[] | undefined,
  maxValues: (number | null)[] | undefined,
  transforms: EncodingTransform[] | null | undefined,
): {
  minValues: (number | null)[] | undefined;
  maxValues: (number | null)[] | undefined;
} => {
  if (minValues == null || maxValues == null) return { minValues, maxValues };
  const factor = scaleForField(field, transforms);
  if (factor === 1) return { minValues, maxValues };
  const scale = (value: number | null) =>
    typeof value === "number" ? roundScaled(value * factor) : value;
  const scaledMin = minValues.map(scale);
  const scaledMax = maxValues.map(scale);
  return factor < 0
    ? { minValues: scaledMax, maxValues: scaledMin }
    : { minValues: scaledMin, maxValues: scaledMax };
};
